import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowRight, ArrowLeft, Loader2, KeyRound } from "lucide-react";

const DEFAULT_LOGO = "/logo-qasesores.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    login_logo_url: DEFAULT_LOGO,
    login_background_color: "#E8E6E6",
    primary_color: "#0C314C",
    accent_color: "#45E69A",
  });

  useEffect(() => {
    axios.get(`${API}/config/personalization`)
      .then(res => setSettings(prev => ({ ...prev, ...res.data })))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success("Contraseña actualizada correctamente. Ya puedes iniciar sesión.");
      navigate("/login");
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo restablecer la contraseña. Solicita un nuevo enlace.";
      toast.error(typeof msg === "string" ? msg : "No se pudo restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const logoUrl = settings.login_logo_url || DEFAULT_LOGO;

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      <div className="hidden lg:flex lg:w-2/3 p-12 flex-col justify-center items-start" style={{ backgroundColor: settings.login_background_color }}>
        <img src={logoUrl} alt="Logo" className="h-72 w-auto" />
      </div>

      <div className="w-full lg:w-1/3 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src={logoUrl} alt="Logo" className="h-32 w-auto" />
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold font-['Manrope']" style={{ color: settings.primary_color }}>
                Nueva contraseña
              </CardTitle>
              <CardDescription className="text-slate-500">
                Crea una nueva contraseña para tu cuenta
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!token ? (
                <div className="space-y-6" data-testid="reset-no-token">
                  <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">
                    Este enlace no es válido o está incompleto. Solicita un nuevo enlace de recuperación.
                  </div>
                  <Link to="/forgot-password">
                    <Button variant="outline" className="w-full">Solicitar nuevo enlace</Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700">Nueva contraseña</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="reset-password-input"
                    />
                    <p className="text-xs text-slate-400">Mínimo 6 caracteres.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm" className="text-slate-700">Confirmar contraseña</Label>
                    <Input
                      id="confirm"
                      type="password"
                      placeholder="••••••••"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      data-testid="reset-confirm-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full text-white font-medium"
                    style={{ backgroundColor: settings.primary_color }}
                    disabled={loading}
                    data-testid="reset-submit-button"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                    {loading ? "Guardando..." : "Cambiar contraseña"}
                  </Button>
                  <div className="text-center pt-2">
                    <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
                      <ArrowLeft className="w-3.5 h-3.5" /> Volver a iniciar sesión
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
